import { useState, useEffect, useCallback, useMemo } from "react";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatFunction, parseFunction } from "./helpers";
import { IFlightFlag } from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IFlightFlagSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/**
 * Sepcialized wrapper for "built-in" oc FlightFlag select field.
 * @param props -
 */
export const FlightFlagSelectField = (props: IFlightFlagSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  ///////////////////////////////////////
  // MANAGE FlightFlag LIST
  ///////////////////////////////////////
  const structures = useSelector((state: IRootState) => state.structures);
  const flightFlags = useMemo(() => {
    return structures?.customerStructure?.flightFlags ?? [];
  }, [structures?.customerStructure?.flightFlags]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, flightFlags, valueKey, isMulti),
    [flightFlags, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Flight Flag Selector"}
      isMulti={isMulti}
      options={flightFlags}
      getOptionLabel={(option: IFlightFlag) => `${option.title}`}
      getOptionValue={(option: IFlightFlag) => option?._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select FlightFlag${isMulti && "s"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
